import {AssertNotify, O, RunInAction} from "web-vcore";
import {JournalEntry, JournalSegment} from "../../../Server/DB/journalEntries/@JournalEntry.js";
import {DreamFlawType, EngineConfig_VR, ImageFrameFlawType} from "../../../Server/DB/engineConfigs/@EngineConfig/@EC_VR.js";
import {Remote_GetInfoForNewFrame} from "../../../Utils/Bridge/Bridge_RemoteUserProcesses.js";
import {store} from "../../index.js";
import {Assert, GetPercentFromXToY, GetRandomNumber, string} from "js-vextensions";
import * as THREE from "three";
import {observable} from "mobx";
import {ParseImagePath} from "../../../Utils/Bridge/Bridge_Native/MediaTypes.js";
import {VideoClip, rtcClient} from "../../../Utils/Bridge/Bridge_WebRTC/RTCClient.js";
import {ImageFrame} from "./vr/ImageFrame.js";
import {ignore} from "mobx-sync";

export class RCEffectPlayRequest {
	constructor(data?: Partial<RCEffectPlayRequest>) { Object.assign(this, data); }
	@O accessor time: number; // in ms (unix timestamp)
	@O accessor rightControllerPosition: THREE.Vector3;
	@O accessor rightControllerDirection: THREE.Vector3;
}

export const IMAGE_FRAME_JUMP_DURATION = 500; // in ms

export class VRState {
	GetUserPosition() {
		// atm there is only one scene, so getting is simple
		return this.scene_lhq.userPosition;
	}
	SetUserPosition(pos: {x: number, y: number, z: number}) {
		// atm there is only one scene, so setting is simple
		this.scene_lhq.userPosition = pos;
	}
	@O accessor scene_lhq = new VRSceneState_LHQ();

	@O @ignore accessor showUI = false;
	@O @ignore accessor activeDreamPanel_segmentPath: string|n;

	// right hand
	@O @ignore accessor right_interactionMode = "frames" as Right_InteractionMode;
	@O @ignore accessor isPainting = false;
	@O @ignore accessor snapshotRevealLevel = 0; // 0 = base (just previous snapshot blurred), 1 = all unblurred

	// rc-effects system
	// ==========

	//@O @ignore rcEffects_playRequestsToProcess = observable.array<RCEffectPlayRequest>([], {deep: false}); // requests that have been made, but not yet fulfilled in AlarmClockCatcher comp
	// we don't make this mobx-observable, both because that causes unnecessary mobx "$mirror" tree updatings, and because there's no need (AlarmClockCatcher merely reads this array in its non-reactive useFrame handler)
	rcEffects_playRequestsToProcess = [] as RCEffectPlayRequest[];

	// image frames
	// ==========

	/** 0 = initial, 1 = one second of holding grip button fully-down (half-down would yield 0.5; trigger button instead would yield -1)*/
	@O @ignore accessor imageFrameOffset = 0;
	@O @ignore accessor imageFrames: ImageFrame[] = []; // note: array is kept in chronological order (ie. frames with lower start-offset are always earlier in the array)
	GetActiveImageFrame(config: EngineConfig_VR) {
		return this.imageFrames.find(a=>this.imageFrameOffset >= a.startOffset && this.imageFrameOffset < a.EndOffset(config));
	}
	GetJumpImageFrame(config: EngineConfig_VR): ImageFrame|n {
		return this.imageFrames[this.imageFrames.indexOf(this.GetActiveImageFrame(config) as any) + 1];
	}
	GetPercentThroughJump_WithGracePeriod() {
		return this.GetPercentThroughJump(100); // add grace period of 100ms, for this comp to get re-rendered as the "active frame" (without a flicker/temp-disappearance due to its status as "jump frame" being lost)
	}
	GetPercentThroughJump(gracePeriodAtEndWhereStillReturns1RatherThanNull = 0) {
		const timeSinceJumpStart = Date.now() - (this.imageFrames_lastJumpStartInfo?.time ?? 0);
		if (timeSinceJumpStart >= IMAGE_FRAME_JUMP_DURATION + gracePeriodAtEndWhereStillReturns1RatherThanNull) return null; // no jump active
		return GetPercentFromXToY(0, IMAGE_FRAME_JUMP_DURATION, timeSinceJumpStart);
	}
	GetImageFrameAtOffset(offsetFromCurrent: number, config: EngineConfig_VR) {
		const activeFrame = this.GetActiveImageFrame(config);
		if (activeFrame == null) return null;
		const activeIndex = this.imageFrames.indexOf(activeFrame);
		return this.imageFrames[activeIndex + offsetFromCurrent]
	}

	@O @ignore accessor imageFrames_lastJumpStartInfo: {
		time: number,
		/** Note: This gets cleared once the entry's effects are applied. */
		timerId: number|n,
		ApplyEffectsIfNotYet: ()=>void,
	}|n;

	// flicker
	/** This field is not mobx-reactive; we set it merely so that the ImageFrame comp can know whether flickering is "currently in use" or not. */
	imageFrames_lastFlickerCheckTime = 0
	@O @ignore accessor imageFrames_lastFlickerTime = 0; // in ms
	@O @ignore accessor imageFrames_currentFlickerVisibility = true;

	// remote
	//@O @ignore visibleFramesAtTimeOfLastFlawCheckerPlay = [] as ImageFrame[];
	@O @ignore accessor imageFramesDisplayed_duringRemoteSession = -1;
	@O @ignore accessor timeCredit_duringRemoteSession = -1; // in ms
	@O @ignore accessor memoryPalacePrompts_duringRemoteSession = -1;
}

// Places to find more HDRI options:
// * https://polyhaven.com/hdris
// * https://ambientcg.com/list?type=hdri%2Chdri-element&technique=hdri-bracketed-panorama-horizon-clearing&sort=popular
// * https://hdrmaps.com/freebies
// * https://hdri-skies.com/free-hdris (only a handful)
export const skyFileOptions = [
	"/3D/ZUp/HDRIs/kloppenheim_06_puresky_2k.hdr",
	"/3D/ZUp/HDRIs/kloppenheim_06_puresky_pinktint_2k.hdr",
	"/3D/ZUp/HDRIs/qwantani_dusk_2_puresky_2k.hdr",
	"/3D/ZUp/HDRIs/kloofendal_48d_partly_cloudy_puresky_2k.hdr",
	"/3D/ZUp/HDRIs/DaySkyHDRI065B_2k.hdr", // ambientCG (CC0); converted from its EXR (RGBELoader needs .hdr)
	"/3D/ZUp/HDRIs/turnberg_sky_5_2k.hdr", // HDRMAPS freebie "Turnberg Sky 5"; converted from its EXR (RGBELoader needs .hdr)
	"/3D/ZUp/HDRIs/turnberg_sky_5_4k.hdr", // same, 4k (sharper in VR; ~20MB)
	"/3D/ZUp/HDRIs/turnberg_sky_5_sphere_2k.hdr", // full-sphere bake of the ktx2 dome below (npm start blender.skyDomeToSphere); used for the Blender world + glass reflections
	// ".ktx2" = upper-hemisphere-only dome + procedural haze below the horizon (made by Scripts/Blender/SkyPrep.py; see SkyBackground.tsx)
	"/3D/ZUp/HDRIs/turnberg_sky_5_8k_upper.ktx2", // from the 10k EXR; ~13MB download, ~22MB VRAM (vs ~85MB for the 4k .hdr)
];

export class VRSceneState_LHQ {
	/** Unlike the mapping system, these VR scenes use standard y-up coordinates. */
	@O accessor userPosition: {x: number, y: number, z: number} = {x: 0, y: -2, z: 1};
	@O accessor showFurniture = true;
	/** Name of the HDRI file used for the visible sky background; see SkyBackground.tsx. */
	@O accessor skyFile = skyFileOptions.Last();
	/** Strength of the visible sky background; should match the BG_Camera strength in the Blender world (for the lightmap bakes), unless lighting is re-baked. */
	@O accessor skyIntensity = 1;
	/** Multiplier on the baked-lightmap intensity (LM_SCALE * PI base); 1 = matches the Blender bake preview. */
	@O accessor lightmapIntensity = 1;
}

export const Right_InteractionMode_values = [
	"frames",
	"interact",
	"paint",
] as const;
export type Right_InteractionMode = typeof Right_InteractionMode_values[number];

export function GetSegmentPath(journalEntry: JournalEntry, segment: JournalSegment) {
	return `${journalEntry.id}/${segment.id ?? journalEntry.segments.indexOf(segment)}`;
}